"use client";

import { ArrowRight, Calendar, Check, Stethoscope, Video } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tabs = [{
  value: "network",
  label: "Network",
  title: "High-performance infrastructure.",
  description: "We build robust foundations for your business connectivity. From high-speed fiber backbones to redundant ISP failover systems, we ensure your operations never go offline.",
  services: ["High-speed copper & fiber", "Redundant ISP failover", "5G/4G/LTE backup", "Wide area Wi-Fi coverage", "Building-to-building links", "Scalable network architecture"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/network_infrastructure.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/network_infrastructure.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/network_infrastructure.webp-wvc-sizes",
    alt: "High-speed fiber network infrastructure"
  },
  stat: "99.9%",
  statLabel: "uptime reliability for critical business operations"
}, {
  value: "surveillance",
  label: "Video Surveillance",
  title: "Intelligent security monitoring.",
  description: "Advanced visual security that does more than record. Our AI-powered systems provide real-time alerts and remote access, keeping your assets protected around the clock.",
  services: ["Expandable video storage", "Car tag & face recognition", "Mobile app integration", "Remote TV monitoring", "AI-powered threat alerts", "Advanced playback features"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/video_surveillance_system.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/video_surveillance_system.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/video_surveillance_system.png-wvc-sizes",
    alt: "AI-powered video surveillance interface"
  },
  stat: "24/7",
  statLabel: "active monitoring with intelligent AI alerts"
}, {
  value: "access",
  label: "Access Control",
  title: "Secure, modern entry points.",
  description: "Streamline your facility access with NFC and mobile-first authentication. Our systems integrate seamlessly with your security infrastructure for total control.",
  services: ["NFC card & tag support", "Mobile phone authentication", "Apple Passkey integration", "Video-enabled door readers", "Intercom system integration", "Automated gate recognition"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/access_control_tech.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/access_control_tech.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/access_control_tech.png-wvc-sizes",
    alt: "Modern NFC access control reader"
  },
  stat: "Secure",
  statLabel: "authentication for modern enterprise facilities"
}, {
  value: "voip",
  label: "VoIP Systems",
  title: "Unified communication tools.",
  description: "Feature-rich communication platforms designed for the modern office. Experience crystal-clear internal video calling and customizable phone systems.",
  services: ["Customizable VoIP solutions", "Large screen video phones", "Integrated camera support", "Internal video calling", "Cloud-based management", "Scalable user extensions"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/voip_communication.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/voip_communication.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/voip_communication.png-wvc-sizes",
    alt: "Enterprise VoIP communication hardware"
  },
  stat: "Unified",
  statLabel: "communication across all your business devices"
}, {
  value: "integrated",
  label: "Integrated Systems",
  title: "Smart facility management.",
  description: "Beyond IT, we integrate smart facility solutions to enhance your environment, from premium audio systems to EV charging infrastructure.",
  services: ["High-quality audio systems", "Spotify & Apple Music integration", "Interactive digital signage", "EV charging stations", "NFC & app-based charging", "Centralized facility control"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/integrated_facility_tech.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/integrated_facility_tech.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/integrated_facility_tech.webp-wvc-sizes",
    alt: "Integrated smart facility technology"
  },
  stat: "Smart",
  statLabel: "facility management for modern businesses"
}];
const highlights = [{
  icon: Calendar,
  title: "Scalable Design",
  description: "Systems that grow with your business requirements and infrastructure needs."
}, {
  icon: Video,
  title: "Remote Management",
  description: "Monitor and control your entire facility from anywhere in the world."
}, {
  icon: Stethoscope,
  title: "Unified Integration",
  description: "Seamlessly connecting network, security, and communication into one platform."
}];
export default function TechnicalCapabilities() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: tabsRef,
    isVisible: tabsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: highlightsRef,
    isVisible: highlightsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="691" id="technical-capabilities" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`max-w-3xl transition-all duration-700 ease-out ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-primary/60" />
            <span className="text-xs font-medium uppercase tracking-[0.22em] text-primary font-default">
              Betaquest, LLC
            </span>
          </div>
          <h2 className="mt-6 font-default text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-[64px]">
            Unified Solutions for Modern Businesses.
          </h2>
          <p className="mt-6 max-w-2xl font-default text-base leading-relaxed text-muted-foreground sm:text-lg">
            Betaquest, LLC specializes in unified IT solutions that are tailored to your business needs. Our systems integrate seamlessly to improve performance, security, and scalability.
          </p>
        </div>

        <div ref={tabsRef} className={`mt-16 transition-all duration-700 ease-out ${tabsVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <Tabs defaultValue="network" className="w-full">
            <div className="border-b border-border">
              <TabsList className="flex h-auto w-full justify-start gap-2 overflow-x-auto bg-transparent p-0 md:gap-8">
                {tabs.map((tab, i) => <TabsTrigger key={tab.value} data-index={i} value={tab.value} className="relative h-auto flex-none whitespace-nowrap border-0 border-b-2 border-transparent bg-transparent px-1 py-4 font-default text-base font-medium text-muted-foreground transition-colors duration-300 hover:text-primary data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none md:text-lg">
                    {tab.label}
                  </TabsTrigger>)}
              </TabsList>
            </div>

            {tabs.map((tab, i) => <TabsContent key={tab.value} data-index={i} value={tab.value} className="mt-12 animate-in fade-in slide-in-from-bottom-2 duration-500 md:mt-16">
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-16">
                  <div className="relative lg:col-span-2">
                    <div className="overflow-hidden rounded-lg bg-muted">
                      <img src={tab.image.src} data-wvc-srcset={tab.image.srcset} data-wvc-sizes={tab.image.sizes} alt={tab.image.alt} loading="lazy" decoding="async" className="aspect-[4/5] h-full w-full object-cover" />
                    </div>
                    <Card className="absolute -bottom-6 -right-4 w-[200px] rounded-lg border border-border bg-card shadow-lg md:-right-8 md:w-[220px]">
                      <CardContent className="px-5 py-5">
                        <div className="font-default text-3xl font-bold text-primary md:text-4xl">
                          {tab.stat}
                        </div>
                        <p className="mt-2 font-default text-xs leading-relaxed text-muted-foreground">
                          {tab.statLabel}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="lg:col-span-3 lg:pl-4">
                    <h3 className="font-default text-2xl font-bold leading-tight text-foreground sm:text-3xl">
                      {tab.title}
                    </h3>
                    <p className="mt-5 font-default text-base leading-relaxed text-muted-foreground">
                      {tab.description}
                    </p>

                    <ul className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                      {tab.services.map((service, j) => <li key={service} data-index={j} className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-md bg-primary/20">
                            <Check className="h-3 w-3 text-primary" strokeWidth={2.5} />
                          </span>
                          <span className="font-default text-sm text-foreground">
                            {service}
                          </span>
                        </li>)}
                    </ul>

                    <div className="mt-10">
                      <Button asChild variant="link" className="group h-auto p-0 font-default text-sm font-bold text-primary hover:no-underline">
                        <Link to="/SolutionsPage">
                          View technical specs
                          <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>

        <Separator className="mt-20 bg-border md:mt-28" />

        <div ref={highlightsRef} className={`mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8 transition-all duration-700 ease-out ${highlightsVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          {highlights.map((item, i) => {
          const Icon = item.icon;
          return <Card key={item.title} data-index={i} className="border border-border bg-card shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg">
                <CardContent className="flex items-start gap-4 px-6 py-6">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-primary/20">
                    <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h4 className="font-default text-lg font-bold text-foreground">
                      {item.title}
                    </h4>
                    <p className="mt-1.5 font-default text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}